package com.evoapps.evoshift.ui.documents

import com.evoapps.evoshift.widgets.SurfaceCard
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.documents.PickKind
import com.evoapps.evoshift.documents.asPickedFile
import com.evoapps.evoshift.documents.documentDropSpec
import com.evoapps.evoshift.filedrop.FileDropArea
import com.evoapps.evoshift.filedrop.FileDropTags
import evoshift.composeapp.generated.resources.policies_drop
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Groups
import androidx.compose.material.icons.filled.Policy
import androidx.compose.material.icons.filled.UploadFile
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.documents.rememberDocumentPicker
import com.evoapps.evoshift.domain.model.PolicyStatus
import com.evoapps.evoshift.domain.model.WorkplacePolicy
import com.evoapps.evoshift.ui.adaptive.ContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.PoliciesViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoTopBar
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.close
import evoshift.composeapp.generated.resources.delete
import evoshift.composeapp.generated.resources.documents_open
import evoshift.composeapp.generated.resources.policies_add
import evoshift.composeapp.generated.resources.policies_add_title
import evoshift.composeapp.generated.resources.policies_confirm_action
import evoshift.composeapp.generated.resources.policies_confirm_statement
import evoshift.composeapp.generated.resources.policies_confirm_title
import evoshift.composeapp.generated.resources.policies_confirmed_on
import evoshift.composeapp.generated.resources.policies_delete_confirm
import evoshift.composeapp.generated.resources.policies_delete_title
import evoshift.composeapp.generated.resources.policies_empty
import evoshift.composeapp.generated.resources.policies_intro
import evoshift.composeapp.generated.resources.policies_not_signature
import evoshift.composeapp.generated.resources.policies_nobody_pending
import evoshift.composeapp.generated.resources.policies_not_yet
import evoshift.composeapp.generated.resources.policies_pending_people
import evoshift.composeapp.generated.resources.policies_publish_action
import evoshift.composeapp.generated.resources.policies_published_on
import evoshift.composeapp.generated.resources.policies_publishing
import evoshift.composeapp.generated.resources.policies_read_action
import evoshift.composeapp.generated.resources.policies_read_count
import evoshift.composeapp.generated.resources.policies_report_title
import evoshift.composeapp.generated.resources.policies_title
import evoshift.composeapp.generated.resources.policies_title_field
import evoshift.composeapp.generated.resources.policies_who_confirmed
import evoshift.composeapp.generated.resources.policies_confirmed_people
import org.jetbrains.compose.resources.stringResource

/**
 * SH-122: the workplace's policies (rules, OHS instruction). Everyone opens the PDF and confirms
 * "I have read it"; managers publish PDFs and see who confirmed and when. The screen says that a
 * confirmation is not a qualified electronic signature.
 */
@Composable
fun PoliciesScreen(
    viewModel: PoliciesViewModel,
    onNavigateBack: () -> Unit
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val shown = (state.error ?: state.message)?.asString()
    LaunchedEffect(shown) {
        shown?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }
    val picker = rememberDocumentPicker { viewModel.onPicked(it) }
    var deleting by remember { mutableStateOf<WorkplacePolicy?>(null) }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = { EvoTopBar(title = stringResource(Res.string.policies_title), onBack = onNavigateBack) }
    ) { padding ->
        // SH-233: a PDF dropped anywhere on the screen (web, desktop, Android and ChromeOS) goes the chooser's way.
        FileDropArea(
            spec = documentDropSpec(PickKind.PDF),
            dropLabel = stringResource(Res.string.policies_drop),
            onFile = { viewModel.onPicked(it.asPickedFile()) },
            modifier = Modifier.padding(padding).fillMaxSize(),
            enabled = state.canPublish && state.publishing == null && state.draft == null && !state.isLoading,
            snackbarHostState = snackbarHostState,
            tag = FileDropTags.POLICIES
        ) {
        Box(Modifier.fillMaxSize().contentWidth(ContentMaxWidth)) {
            if (state.isLoading) {
                CircularProgressIndicator(Modifier.align(Alignment.Center))
                return@Box
            }
            Column(
                modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()).padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text(stringResource(Res.string.policies_intro), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                if (state.canPublish) {
                    Button(onClick = { picker.pick(pdfOnly = true) }, enabled = state.publishing == null) {
                        Icon(Icons.Default.UploadFile, contentDescription = null)
                        Spacer(Modifier.width(8.dp))
                        Text(stringResource(Res.string.policies_add))
                    }
                }
                state.publishing?.let { progress ->
                    Text(stringResource(Res.string.policies_publishing), style = MaterialTheme.typography.bodySmall)
                    LinearProgressIndicator(progress = { progress }, modifier = Modifier.fillMaxWidth())
                }
                if (state.policies.isEmpty() && state.publishing == null) {
                    Text(stringResource(Res.string.policies_empty), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
                state.policies.forEach { status ->
                    PolicyCard(
                        status = status,
                        opening = state.openingId == status.policy.id,
                        readCount = state.readCounts[status.policy.id],
                        onOpen = { viewModel.open(status.policy) },
                        onConfirm = if (state.canConfirm && !status.isAcknowledged) { { viewModel.askToConfirm(status.policy) } } else null,
                        onReport = if (state.canSeeReport) { { viewModel.showReport(status.policy) } } else null,
                        onDelete = if (state.canPublish) { { deleting = status.policy } } else null
                    )
                }
                Text(stringResource(Res.string.policies_not_signature), style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        }
        }
    }

    state.confirming?.let { policy ->
        EvoDialog(
            title = stringResource(Res.string.policies_confirm_title),
            icon = Icons.Default.CheckCircle,
            onDismiss = { viewModel.cancelConfirm() },
            width = DialogWidth.Compact,
            confirm = DialogAction(stringResource(Res.string.policies_confirm_action), { viewModel.confirm() })
        ) {
            Text(stringResource(Res.string.policies_confirm_statement, policy.title), color = Color.White.copy(alpha = 0.9f))
            Text(stringResource(Res.string.policies_not_signature), color = Color.White.copy(alpha = 0.65f), style = MaterialTheme.typography.bodySmall)
        }
    }

    state.draft?.let { draft ->
        EvoDialog(
            title = stringResource(Res.string.policies_add_title),
            icon = Icons.Default.Policy,
            onDismiss = { viewModel.cancelDraft() },
            dismissOnClickOutside = false,
            confirm = DialogAction(stringResource(Res.string.policies_publish_action), { viewModel.publish() }, enabled = draft.title.isNotBlank())
        ) {
            Text(draft.file.name.orEmpty(), color = Color.White, fontWeight = FontWeight.Bold)
            DialogSection(null) {
                EvoTextField(
                    value = draft.title,
                    onValueChange = { viewModel.setDraftTitle(it) },
                    label = stringResource(Res.string.policies_title_field),
                    imeAction = ImeAction.Done
                )
            }
        }
    }

    state.report?.let { report ->
        EvoDialog(
            title = stringResource(Res.string.policies_report_title),
            subtitle = report.policy.title,
            icon = Icons.Default.Groups,
            onDismiss = { viewModel.hideReport() },
            dismiss = null,
            confirm = DialogAction(stringResource(Res.string.close), { viewModel.hideReport() })
        ) {
            DialogSection(stringResource(Res.string.policies_confirmed_people)) {
                report.acknowledged.forEach { ack ->
                    Text(
                        stringResource(Res.string.policies_who_confirmed, ack.displayName.orEmpty(), formatMoment(ack.acknowledgedAt)),
                        color = Color.White.copy(alpha = 0.9f)
                    )
                }
            }
            DialogSection(stringResource(Res.string.policies_pending_people)) {
                if (report.pending.isEmpty()) {
                    Text(stringResource(Res.string.policies_nobody_pending), color = Color.White.copy(alpha = 0.7f))
                }
                report.pending.forEach { (_, name) -> Text(name, color = Color.White.copy(alpha = 0.9f)) }
            }
        }
    }

    deleting?.let { policy ->
        EvoDialog(
            title = stringResource(Res.string.policies_delete_title),
            icon = Icons.Default.Delete,
            onDismiss = { deleting = null },
            destructive = true,
            width = DialogWidth.Compact,
            confirm = DialogAction(stringResource(Res.string.delete), { viewModel.delete(policy); deleting = null }, destructive = true)
        ) {
            Text(stringResource(Res.string.policies_delete_confirm, policy.title), color = Color.White.copy(alpha = 0.85f))
        }
    }
}

@Composable
private fun PolicyCard(
    status: PolicyStatus,
    opening: Boolean,
    readCount: Pair<Int, Int>?,
    onOpen: () -> Unit,
    onConfirm: (() -> Unit)?,
    onReport: (() -> Unit)?,
    onDelete: (() -> Unit)?
) {
    val policy = status.policy
    SurfaceCard(
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Default.Policy, contentDescription = null, tint = MaterialTheme.colorScheme.primary)
                Spacer(Modifier.width(8.dp))
                Text(policy.title, fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                if (onDelete != null) {
                    IconButton(onClick = onDelete, modifier = Modifier.size(36.dp)) {
                        Icon(Icons.Default.Delete, contentDescription = stringResource(Res.string.policies_delete_title))
                    }
                }
            }
            policy.publishedAt?.let {
                Text(stringResource(Res.string.policies_published_on, formatMoment(it)), style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
            Text(
                status.acknowledgement?.let { stringResource(Res.string.policies_confirmed_on, formatMoment(it.acknowledgedAt)) }
                    ?: stringResource(Res.string.policies_not_yet),
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = if (status.isAcknowledged) FontWeight.Normal else FontWeight.Bold
            )
            if (onReport != null && readCount != null) {
                HorizontalDivider()
                TextButton(onClick = onReport) {
                    Text(stringResource(Res.string.policies_read_count, readCount.first, readCount.second))
                }
            }
            Row(Modifier.align(Alignment.End), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedButton(onClick = onOpen, enabled = !opening) {
                    if (opening) CircularProgressIndicator(Modifier.size(16.dp), strokeWidth = 2.dp)
                    else Text(stringResource(Res.string.documents_open))
                }
                if (onConfirm != null) {
                    Button(onClick = onConfirm) { Text(stringResource(Res.string.policies_read_action)) }
                }
            }
        }
    }
}
